import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { useTalentSearch } from '../../hooks/useTalentSearch';
import { useOccupationSearch } from '../../hooks/useOccupations';
import { useSkillsSearch } from '../../hooks/useSkills';
import type { TalentSearchFilters } from '../../api/talentSearch';
import { toast } from 'sonner';

type SavedList = {
  id: string;
  name: string;
  filters: TalentSearchFilters;
  createdAt: string;
};

export function TalentSearchPage() {
  const [filters, setFilters] = useState<TalentSearchFilters>({
    query: '',
    location: '',
    returnerFriendly: true,
    minExperience: 3,
    skills: [],
    occupationId: undefined,
  });
  const [skillsInput, setSkillsInput] = useState('');
  const [savedLists, setSavedLists] = useState<SavedList[]>([]);
  const [gdprAcknowledged, setGdprAcknowledged] = useState(false);

  const talentQuery = useTalentSearch(filters);
  const occupationSearch = useOccupationSearch(filters.query ?? '');
  const skillsSearch = useSkillsSearch(skillsInput);
  const filteredResults = useMemo(() => (talentQuery.data ?? []).slice(0, 12), [talentQuery.data]);
  const roleSuggestions = useMemo(() => {
    if (!occupationSearch.data) return [];
    return occupationSearch.data.slice(0, 10);
  }, [occupationSearch.data]);

  const handleFilterChange = (field: keyof TalentSearchFilters, value: unknown) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const addSkill = () => {
    const clean = skillsInput.trim();
    if (!clean) return;
    if (filters.skills?.includes(clean)) {
      toast.info('Skill already added.');
      return;
    }
    setFilters((prev) => ({ ...prev, skills: [...(prev.skills ?? []), clean] }));
    setSkillsInput('');
  };

  const removeSkill = (skill: string) => {
    setFilters((prev) => ({ ...prev, skills: prev.skills?.filter((item) => item !== skill) }));
  };

  const saveList = () => {
    const name = window.prompt('Name this talent list', filters.query ? `${filters.query} candidates` : 'New list');
    if (!name) return;
    const list: SavedList = {
      id: crypto.randomUUID?.() ?? `list-${Date.now()}`,
      name: name.trim(),
      filters,
      createdAt: new Date().toISOString(),
    };
    setSavedLists((prev) => [list, ...prev]);
    toast.success('Talent list saved.');
  };

  return (
    <main className="min-h-screen bg-[#070917] pb-24" aria-labelledby="talent-search-title">
      <header className="border-b border-white/10 bg-[#0d1022]/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-10 sm:px-6 lg:px-8">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-gray-500">Employer · Talent Search</p>
            <h1 id="talent-search-title" className="text-3xl font-semibold text-white sm:text-4xl">Find returner-ready talent</h1>
            <p className="text-sm text-gray-400">
              Search Lighthouse talent pools, filter by experience, and build GDPR-compliant outreach lists.
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 px-4 py-10 sm:px-6 lg:px-8">
        {!gdprAcknowledged && (
          <Card className="border-[#d2ac47]/40 bg-[#ba9036]/10">
            <CardHeader>
              <CardTitle className="text-white">GDPR reminder</CardTitle>
              <CardDescription className="text-gray-100">
                Outreach is limited to candidates who opted-in. Keep messaging respectful and record consent.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-4">
              <Button
                variant="outline"
                className="border-[#f7ef8a]/60 text-[#f7ef8a] hover:bg-[#f7ef8a]/10"
                onClick={() => setGdprAcknowledged(true)}
              >
                Acknowledge + continue
              </Button>
              <Button variant="ghost" className="text-gray-200 hover:text-white" asChild>
                <a href="/privacy" target="_blank" rel="noreferrer">
                  Read policy
                </a>
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="border-white/10 bg-white/[0.03]">
          <CardHeader>
            <CardTitle className="text-white">Filters</CardTitle>
            <CardDescription className="text-gray-400">Tune your search to surface the right candidates.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm text-gray-300">Role or skill</label>
                <Input
                  value={filters.query ?? ''}
                  onChange={(event) => handleFilterChange('query', event.target.value)}
                  className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                  placeholder="e.g. care assistant, leadership"
                />
                {roleSuggestions.length > 0 && (
                  <div className="space-y-1 rounded-lg border border-white/10 bg-black/40 p-2">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">Occupation suggestions</p>
                    <div className="flex flex-wrap gap-2">
                      {roleSuggestions.map((role) => (
                        <Button
                          key={role.id}
                          type="button"
                          variant="outline"
                          className="border-white/20 bg-white/5 text-xs text-white hover:bg-white/10"
                          onClick={() => {
                            setFilters((prev) => ({
                              ...prev,
                              query: role.preferredLabel,
                              occupationId: role.id,
                            }));
                          }}
                        >
                          {role.preferredLabel}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-300">Location</label>
                <Input
                  value={filters.location ?? ''}
                  onChange={(event) => handleFilterChange('location', event.target.value)}
                  className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                  placeholder="City or region"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm text-gray-300">Minimum experience (years)</label>
                <Input
                  type="number"
                  min={0}
                  value={filters.minExperience ?? 0}
                  onChange={(event) => handleFilterChange('minExperience', Number(event.target.value))}
                  className="border-white/10 bg-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-300">Required skills</label>
                <div className="flex gap-2">
                  <Input
                    value={skillsInput}
                    onChange={(event) => setSkillsInput(event.target.value)}
                    className="flex-1 border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                    placeholder="Comma-separated or add individually"
                  />
                  <Button type="button" variant="outline" className="border-white/20 text-white" onClick={addSkill}>
                    Add
                  </Button>
                </div>
                {skillsSearch.data && skillsInput.trim().length >= 2 && (
                  <div className="space-y-1 rounded-lg border border-white/10 bg-black/40 p-2">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">Skill suggestions</p>
                    <div className="flex flex-wrap gap-2">
                      {skillsSearch.data.slice(0, 8).map((skill) => (
                        <Button
                          key={skill.id}
                          type="button"
                          variant="outline"
                          className="border-white/20 bg-white/5 text-xs text-white hover:bg-white/10"
                          onClick={() => {
                            if (filters.skills?.includes(skill.preferredLabel)) {
                              toast.info('Skill already added.');
                              return;
                            }
                            setFilters((prev) => ({
                              ...prev,
                              skills: [...(prev.skills ?? []), skill.preferredLabel],
                            }));
                            setSkillsInput('');
                          }}
                        >
                          {skill.preferredLabel}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                {filters.skills?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {filters.skills.map((skill) => (
                      <Badge
                        key={skill}
                        className="bg-white/10 text-sm text-gray-100"
                        onClick={() => removeSkill(skill)}
                      >
                        {skill} ×
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="returner-friendly"
                checked={filters.returnerFriendly}
                onCheckedChange={(checked) => handleFilterChange('returnerFriendly', Boolean(checked))}
              />
              <label htmlFor="returner-friendly" className="text-sm text-gray-300">
                Only show returner-friendly candidates
              </label>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                className="bg-gold-gradient text-black hover:brightness-110"
                onClick={() => talentQuery.refetch()}
                disabled={talentQuery.isFetching}
              >
                {talentQuery.isFetching ? 'Searching…' : 'Refresh results'}
              </Button>
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={saveList}>
                Save list
              </Button>
              <Button
                variant="ghost"
                className="text-gray-400 hover:text-white"
                onClick={() =>
                  setFilters({ query: '', location: '', returnerFriendly: true, minExperience: 3, skills: [], occupationId: undefined })
                }
              >
                Reset filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {savedLists.length > 0 && (
          <Card className="border-white/10 bg-white/[0.02]">
            <CardHeader>
              <CardTitle className="text-white">Saved lists</CardTitle>
              <CardDescription className="text-gray-400">Reopen previous searches in a click.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3 text-sm text-gray-300">
              {savedLists.map((list) => (
                <Button
                  key={list.id}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/10"
                  onClick={() => setFilters(list.filters)}
                >
                  {list.name}
                </Button>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {talentQuery.isPending ? (
            <Card className="border-white/10 bg-white/[0.02]">
              <CardContent className="p-10 text-center text-sm text-gray-300">Loading candidates…</CardContent>
            </Card>
          ) : filteredResults.length === 0 ? (
            <Card className="border-white/10 bg-white/[0.02]">
              <CardContent className="p-10 text-center text-sm text-gray-300">
                No candidates match these filters yet. Adjust your criteria or contact Lighthouse support.
              </CardContent>
            </Card>
          ) : (
            filteredResults.map((candidate) => (
              <Card key={candidate.id} className="border-white/10 bg-white/[0.03]">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-white">
                    {candidate.name}
                    <Badge className="bg-gold-gradient text-black">{candidate.matchScore}% match</Badge>
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    {candidate.title} • {candidate.location} • {candidate.experienceYears}+ yrs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-gray-300">
                  <p className="whitespace-pre-line">{candidate.bio}</p>
                  <div className="flex flex-wrap gap-2">
                    {candidate.skills.map((skill) => (
                      <Badge key={skill} className="border-white/10 bg-white/[0.08] text-gray-200">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400">
                    Availability: {candidate.availability} • Salary: {candidate.salaryRange}
                  </p>
                  <Textarea
                    rows={3}
                    placeholder="Add outreach notes or questions"
                    className="border-white/10 bg-white/5 text-white placeholder:text-gray-400"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                      Message
                    </Button>
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                      Shortlist
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
    </main>
  );
}
